import classNames from "classnames";
import React, { type JSX, useState } from "react";
import { t } from "ttag";

import {
    IReviewQueryFacets,
    ISearchFacetOption,
} from "../../../models/reviews";
import { parseFacetValues } from "../../../utils/reviews";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { updateFilterOptionCollectionValue } from "../reducers";
import { FilterOption } from "./FilterOption";

import styles from "./FilterOptionCollection.module.scss";

interface IProps {
    facetType: IReviewQueryFacets;
    name: string | null;
    options: readonly ISearchFacetOption[];
    isOpen: boolean;
    isCarouselComponentType: boolean;
    isCollapsedView?: boolean;
    isCondensedView?: boolean;
}

const buildOptions = (
    options: readonly ISearchFacetOption[],
): JSX.Element[] => {
    return options.reduce<JSX.Element[]>((memo, option, i) => {
        // If suboptions exist, only show them
        const subOptionType = option.sub_option_type;
        if (subOptionType && option.sub_options.length > 0) {
            const subOptions = option.sub_options.map((subOption, j) => (
                <FilterOption
                    key={`${i}.${j}`}
                    facetType={subOptionType}
                    option={subOption}
                    showCount={false}
                />
            ));
            memo = memo.concat(subOptions);
        } else {
            memo.push(
                <FilterOption
                    key={i}
                    facetType={"product_id"}
                    option={option}
                    showCount={true}
                />,
            );
        }
        return memo;
    }, []);
};

const calculateTotalCount = (
    options: ReadonlyArray<ISearchFacetOption>,
): number => {
    return options.map((col) => col.quantity).reduce((a, b) => a + b, 0);
};

const generateCollectionID = (collectionName: string): string => {
    return collectionName.toLowerCase().replace(/[^a-zA-Z0-9]+/g, "-");
};

const calculateIsChecked = (
    options: ReadonlyArray<ISearchFacetOption>,
    currentOptionIDs: string[],
    currentSubOptionIDs: string[],
): boolean => {
    const collectionOptionIDs = options.map((col) => `${col.option_id}`);
    const collectionSubOptionIDs = options.reduce<string[]>((memo, option) => {
        const subOptionIDs = option.sub_options.map(
            (variant) => `${variant.option_id}`,
        );
        return memo.concat(subOptionIDs);
    }, []);

    const allOptionChecked = collectionOptionIDs.every((id) =>
        currentOptionIDs.includes(id),
    );
    const allSubOptionsChecked = collectionSubOptionIDs.every((id) =>
        currentSubOptionIDs.includes(id),
    );

    return (
        (collectionOptionIDs.length > 0 && allOptionChecked) ||
        (collectionSubOptionIDs.length > 0 && allSubOptionsChecked)
    );
};

export const FilterOptionCollection = (props: IProps): React.ReactNode => {
    const [collectionOpen, setCollectionOpen] = useState(false);
    const [variantListOpen, setVariantListOpen] = useState(
        !props.isCondensedView,
    );

    const dispatch = useAppDispatch();

    const reviewsState = useAppSelector((rootState) => rootState.reviews);

    const currentOptionIDs = parseFacetValues(
        reviewsState.ui.facetValues.product_id,
    );
    const currentSubOptionIDs = parseFacetValues(
        reviewsState.ui.facetValues.product_variant_id,
    );

    const isChecked = calculateIsChecked(
        props.options,
        currentOptionIDs,
        currentSubOptionIDs,
    );

    const onToggleCollection = () => {
        setCollectionOpen((prev) => !prev);
    };

    const onToggleListClick = () => {
        if (!props.isCondensedView) {
            return;
        }
        setVariantListOpen((prev) => !prev);
    };

    const onChange = (e: React.FormEvent<HTMLInputElement>) => {
        onToggleListClick();
        dispatch(
            updateFilterOptionCollectionValue({
                facetType: props.facetType,
                options: props.options,
                isSelected: e.currentTarget.checked,
            }),
        );
    };

    const buildCollectionHeader = (collectionName: string): JSX.Element => {
        const categoryClasses = classNames({
            [styles.filterTitle]: true,
            [styles.filterTitleCategory]: true,
        });
        const collectionID = generateCollectionID(collectionName);
        const count = calculateTotalCount(props.options);
        const checkboxClasses = classNames({
            [styles.checkbox]: true,
            [styles.checkboxCategory]: true,
        });
        const isCheckedWithVariantList =
            isChecked || (!!props.isCondensedView && variantListOpen);

        return (
            <div>
                <fieldset className={categoryClasses}>
                    <legend className="ada-screenreader-only">
                        Choose your interests
                    </legend>
                    <input
                        id={collectionID}
                        type="checkbox"
                        checked={isCheckedWithVariantList}
                        onChange={onChange}
                        className={checkboxClasses}
                    />
                    <label htmlFor={collectionID}>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: collectionName,
                            }}
                        />
                        &nbsp;
                        <span>({count})</span>
                    </label>
                </fieldset>
            </div>
        );
    };

    const collapsedViewAndOpen = !props.isCollapsedView || collectionOpen;
    const listClasses = classNames({
        [styles.list]: true,
        [styles.listParentOpen]: props.isOpen || variantListOpen,
        [styles.listOpen]: props.isOpen,
        [styles.indentedList]: props.isCondensedView || false,
    });
    const toggleClasses = classNames({
        [styles.list]: true,
        [styles.titleToggle]: true,
        [styles.titleToggleOpen]: collapsedViewAndOpen,
        [styles.listOpen]: props.isOpen,
    });
    const toggleOpenClasses = classNames({
        [styles.list]: true,
        [styles.listOpen]: true,
    });
    const collectionName = props.name || "";

    return (
        <>
            {props.isCollapsedView && (
                <button
                    className={toggleClasses}
                    onClick={onToggleCollection}
                    aria-expanded={collectionOpen}
                    aria-controls={"collection-side-filters"}
                >
                    <span
                        dangerouslySetInnerHTML={{ __html: collectionName }}
                    ></span>
                </button>
            )}
            <span className="ada-screenreader-only">
                {t`Activating this element will cause content on the page to be updated`}
            </span>
            {collapsedViewAndOpen && (
                <ul className={listClasses} id={"collection-side-filters"}>
                    <li>
                        {!props.isCarouselComponentType &&
                            buildCollectionHeader(collectionName)}
                        {variantListOpen && (
                            <ul className={toggleOpenClasses}>
                                {buildOptions(props.options)}
                            </ul>
                        )}
                    </li>
                </ul>
            )}
        </>
    );
};
